// Lend report tabs - a fixed 19-section report over lend_performance_report_2
// (a separate, already-populated collection - see generate_platform_report.js's
// fetchLendReportDoc), not report_calc_service's {platform}_platform_report
// shape ReportDocTabs/buildTabsForReportDoc render. Every field name/shape
// referenced here was verified against a real document and cross-checked
// against the reference report (Lend Performance Report 202606.pdf) before
// use - see pdf_generation_service's pdf_generation/_charts/lend.py, which
// builds the same sections for the PDF from the same fields, so the two
// surfaces tell the same story.
//
// The PBIX's per-page segment slicer (Private/SME/Mortgage/[Total]) is
// reproduced here as <SegmentToggle> - a local SelectButton picking which of
// the already-fetched doc's per-segment fields to render. No new fetch, no
// new backend call: this is exactly what the PowerBI slicer did (show a
// different already-computed series), so it costs nothing calculation-wise.
import React, { useEffect, useMemo, useState } from 'react';
import { Chart as ChartJS } from 'chart.js';
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton } from 'primereact/selectbutton';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import { Slider } from 'primereact/slider';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';
import axiosWithAuth from '../../helpers/AxiosHelper';
import { CHART_COLORS, cyclePalette, BASE_OPTIONS, chartRemountKey, formatLegendText } from './chart_theme';
import { useTheme } from '../../hooks/useTheme';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import './platform_report.css';

// Chart.js core (which PrimeReact's <Chart> wrapper lazy-loads as
// 'chart.js/auto') has no box-and-whisker chart type at all - not a gap in
// how it's configured, the type doesn't exist in the library. Plain
// Chart.js bar/line datasets (used for every other chart in this file)
// can't represent a 5-number-summary distribution (min/Q1/median/Q3/max)
// per category; a bar can only encode one value per category, and faking
// whiskers with overlapping floating bars doesn't give a real median line
// or outlier rendering. @sgratzl/chartjs-chart-boxplot is the actively
// maintained fork of the original chartjs-chart-box-and-violin-plot,
// built for Chart.js v4 (matching the v4 already pinned in package.json),
// MIT-licensed - it registers a new controller/element onto the same
// global Chart.js instance PrimeReact's wrapper uses (registration is a
// module-singleton, so this doesn't need to coordinate with the wrapper's
// own dynamic import), rather than requiring a competing charting library.
ChartJS.register(BoxPlotController, BoxAndWiskers);

// Segment colors matching the reference report's legend (Private = teal,
// SME = navy, Mortgage = gray) - see pdf_generation/_charts/lend.py's
// _SEGMENT_COLORS, kept in sync manually the same way that module's own
// header comment already documents for the PDF/dashboard pair. Genuinely
// Lend-only (no other platform has per-borrower-type colors), unlike
// CHART_COLORS/cyclePalette/BASE_OPTIONS above, which are shared - see
// chart_theme.js.
// Mutated in place by applyLendColorsTheme (called from LendDocTabs below)
// exactly like chart_theme.js's own CHART_COLORS.categorical - sme's navy
// (#0f1942) has ~1.03:1 contrast against the dark theme's card background
// (#111833), functionally invisible, so it's remapped to a lighter shade in
// dark mode; private/mortgage are unaffected. Every consumer below reads
// this object's current property values at render/build time (including
// via `get color()` getters on the module-level *_SERIES arrays further
// down, specifically so those don't freeze a snapshot of .sme at import
// time) - see applyLendColorsTheme's own docstring.
const LIGHT_SEGMENT_COLORS = { private: '#077186', sme: '#0f1942', mortgage: '#8a8f98' };
const SEGMENT_COLORS = { ...LIGHT_SEGMENT_COLORS };
const SEGMENT_DARK_OVERRIDES = { sme: '#7881a5' }; // same computed replacement as chart_theme.js's DARK_COLOR_OVERRIDES['#0f1942']
function applyLendColorsTheme(theme) {
    const isDark = theme === 'dark';
    Object.keys(LIGHT_SEGMENT_COLORS).forEach((key) => {
        SEGMENT_COLORS[key] = (isDark && SEGMENT_DARK_OVERRIDES[key]) || LIGHT_SEGMENT_COLORS[key];
    });
}
const SEGMENT_LABELS = { private: 'Private', sme: 'SME', mortgage: 'Mortgage', total: 'All Loans' };

// ChartCard/OverviewTable/MatrixTable/PortfolioStatsTable are also used by
// MozzenoReportSections.js (same report family, same table shapes).
export function ChartCard({ title, children, height = 320 }) {
    return (
        <div className="pr-card pr-chart-card flex flex-column mb-3" style={{ height: height + 56 }}>
            <h5 className="pr-card-title flex-shrink-0">{title}</h5>
            <div className="flex-grow-1" style={{ minHeight: 0 }}>{children}</div>
        </div>
    );
}

// "44.49%" -> 44.49. Passes plain numbers through unchanged - mirrors
// pdf_generation/_charts/lend.py's _pct_to_float exactly, same reasoning:
// use the doc's own pre-computed percentage strings directly rather than
// re-deriving them.
function pctToFloat(value) {
    if (typeof value === 'string') return parseFloat(value.replace('%', ''));
    return value;
}

function SegmentToggle({ value, onChange, segments = ['private', 'sme', 'mortgage'], includeTotal = false }) {
    const options = [...segments, ...(includeTotal ? ['total'] : [])].map((s) => ({ label: SEGMENT_LABELS[s], value: s }));
    return <SelectButton value={value} options={options} onChange={(e) => (e.value ? onChange(e.value) : null)} className="mb-3 pr-segment-toggle" />;
}

// ============================================================================
// Tables
// ============================================================================

// General Overview - one segment's {index, "Most Recent Indicators": label,
// "Lend <Segment>": value} rows, already pre-formatted display strings.
export function OverviewTable({ rows, labelKey = 'Most Recent Indicators' }) {
    if (!rows?.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    const valueKey = Object.keys(rows[0]).find((k) => k !== 'index' && k !== labelKey);
    return (
        <DataTable value={rows} size="small" showHeaders={false} className="pr-kv-table">
            <Column field={labelKey} bodyClassName="pr-cell-muted" />
            <Column field={valueKey} align="right" bodyClassName="pr-cell-strong" />
        </DataTable>
    );
}

// Generic "label column + N pre-formatted data columns" table - covers
// Monthly Return Matrix, Risk Metrics, Interest Rate Distribution, Coupons
// by Vintage. Mirrors pdf_generation/utils/lend_formatters.py's
// lend_matrix_table_to_latex: values are used as-is, only the column set is
// derived from the data (every row is expected to share the same keys).
export function MatrixTable({ rows, labelCol, labelHeader }) {
    if (!rows?.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    const dataCols = Object.keys(rows[0]).filter((k) => k !== labelCol);
    return (
        <div className="pr-table-frame mb-3">
            <DataTable value={rows} size="small" stripedRows>
                <Column field={labelCol} header={labelHeader} bodyClassName="pr-cell-strong" />
                {dataCols.map((col) => (
                    <Column key={col} field={col} header={col} align="right" />
                ))}
            </DataTable>
        </div>
    );
}

// "Selected Active Portfolio Statistics since Launch" - merges each
// segment's own {index, "0": value} rows into one table keyed by the shared
// "index" (metric name), one column per segment. Mirrors
// lend_formatters.py's lend_portfolio_stats_table_to_latex.
export function PortfolioStatsTable({ statsBySegment }) {
    const byMetric = new Map();
    Object.entries(statsBySegment).forEach(([label, rows]) => {
        (rows || []).forEach((row) => {
            if (!byMetric.has(row.index)) byMetric.set(row.index, { 'Key Indicators': row.index });
            byMetric.get(row.index)[label] = row['0'];
        });
    });
    const merged = Array.from(byMetric.values());
    if (!merged.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return (
        <div className="pr-table-frame mb-3">
            <DataTable value={merged} size="small" stripedRows>
                <Column field="Key Indicators" bodyClassName="pr-cell-strong" />
                {Object.keys(statsBySegment).map((label) => (
                    <Column key={label} field={label} header={label} align="right" />
                ))}
            </DataTable>
        </div>
    );
}

// ============================================================================
// Charts
// ============================================================================

// Stacked bar from a list of flat dicts - `series` is [{key, label, color}],
// `xField` the category field. Covers Origination Volume (by year/by month).
function StackedBarChart({ rows, series, xField }) {
    const data = useMemo(
        () => ({
            labels: rows.map((row) => row[xField]),
            datasets: series
                .filter((s) => rows.some((row) => row[s.key] !== undefined))
                .map((s) => ({ label: formatLegendText(s.label), backgroundColor: s.color, data: rows.map((row) => row[s.key] || 0) })),
        }),
        [rows, series, xField]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: { x: { stacked: true, ticks: { maxRotation: 90, minRotation: 90 } }, y: { stacked: true } },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// One line per segment, from the sibling index doc's AverageLoanYield -
// "Average Interest Rates of Monthly Loan Origination Vintages".
//
// Each segment's rows are aligned to one shared, chronologically-sorted
// `labels` list (rather than each dataset carrying its own {x, y} points on
// an implicit category axis) - Chart.js's category scale otherwise orders
// categories by first-encounter across datasets, not by value, which for
// three segments with different date ranges produced an unsorted x-axis
// with lines zigzagging back and forth across the whole chart.
function InterestRateVintageChart({ indexDoc }) {
    const data = useMemo(() => {
        const segments = [
            ['mortgage', 'mortgage'],
            ['consumer', 'private'],
            ['sme', 'sme'],
        ].map(([docKey, segKey]) => {
            const rows = (indexDoc?.[docKey] || []).filter((r) => typeof r.AverageLoanYield === 'number');
            return { segKey, byMonth: new Map(rows.map((r) => [r.year_month, r.AverageLoanYield * 100])) };
        });
        const labels = [...new Set(segments.flatMap((s) => [...s.byMonth.keys()]))].sort();
        const datasets = segments
            .filter((s) => s.byMonth.size > 0)
            .map((s) => ({
                label: SEGMENT_LABELS[s.segKey],
                borderColor: SEGMENT_COLORS[s.segKey],
                backgroundColor: SEGMENT_COLORS[s.segKey],
                data: labels.map((m) => (s.byMonth.has(m) ? s.byMonth.get(m) : null)),
                fill: false,
                pointRadius: 0,
                spanGaps: true,
            }));
        return { labels, datasets };
    }, [indexDoc]);
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: { x: { type: 'category' }, y: { ticks: { callback: (v) => `${v}%` } } },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    if (!data.datasets.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="line" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Grouped bar, one series per segment column - covers Outstanding by Credit
// Score and Average Ticket Size.
function GroupedSegmentBarChart({ rows, xField, seriesFields }) {
    const data = useMemo(
        () => ({
            labels: rows.map((row) => row[xField]),
            datasets: seriesFields
                .filter((s) => rows.some((row) => row[s.key] !== undefined))
                .map((s) => ({ label: formatLegendText(s.label), backgroundColor: s.color, data: rows.map((row) => row[s.key] || 0) })),
        }),
        [rows, xField, seriesFields]
    );
    const options = useMemo(
        () => ({ ...BASE_OPTIONS, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } } }),
        []
    );
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Borrower rate distribution by month - a true 5-number-summary
// (min/Q1/median/Q3/max) per month, pre-computed server-side (AQL
// PERCENTILE, see backend/api/lend_loanbook.js) since the underlying
// lend_format_for_index collection (284k rows) is far too large to ship
// to the browser for a client-side quartile calculation. `@sgratzl/
// chartjs-chart-boxplot` accepts pre-computed {min,q1,median,q3,max}
// objects directly (skips its own stats calculation), which is exactly
// the shape rate_stats_by_month already arrives in.
function BorrowerRateBoxPlotChart({ rows, loanType }) {
    const safeRows = useMemo(() => (rows || []).filter((r) => r.loan_type === loanType), [rows, loanType]);
    const data = useMemo(
        () => ({
            labels: safeRows.map((r) => r.month),
            datasets: [
                {
                    label: 'Borrower Rate',
                    backgroundColor: 'rgba(7, 113, 134, 0.4)',
                    borderColor: '#077186',
                    data: safeRows.map((r) => ({ min: r.lo * 100, q1: r.q1 * 100, median: r.median * 100, q3: r.q3 * 100, max: r.hi * 100 })),
                },
            ],
        }),
        [safeRows]
    );
    const options = useMemo(
        () => ({ ...BASE_OPTIONS, scales: { x: { ticks: { maxRotation: 90, minRotation: 90 } }, y: { ticks: { callback: (v) => `${v}%` } } }, plugins: { legend: { display: false } } }),
        []
    );
    if (!safeRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="boxplot" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Origination volume by purpose - `rows` is the endpoint's
// origination_by_purpose array ({purpose, month, amount}), aggregated
// client-side into totals-by-purpose for the pie, or left month-wise for
// the clustered column.
function OriginationByPurposePieChart({ rows, loanType }) {
    const safeRows = useMemo(() => (loanType === 'All' ? rows || [] : (rows || []).filter((r) => r.loan_type === loanType)), [rows, loanType]);
    const data = useMemo(() => {
        const byPurpose = new Map();
        safeRows.forEach((r) => byPurpose.set(r.purpose, (byPurpose.get(r.purpose) || 0) + r.amount));
        const purposes = [...byPurpose.keys()];
        return {
            // Pie/doughnut legends read data.labels directly (see
            // chart_theme.js's formatLegendText docstring) - the pie
            // controller's own generateLabels override bypasses the global
            // Chart.js default, so this has to be formatted at the source.
            labels: purposes.map((p) => formatLegendText(p)),
            datasets: [{ data: purposes.map((p) => byPurpose.get(p)), backgroundColor: cyclePalette(CHART_COLORS.categorical, purposes.length) }],
        };
    }, [safeRows]);
    const options = useMemo(() => ({ ...BASE_OPTIONS, plugins: { legend: { position: 'right', labels: { boxWidth: 12 } } } }), []);
    if (!safeRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="pie" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function OriginationByPurposeColumnChart({ rows, loanType }) {
    const safeRows = useMemo(() => (loanType === 'All' ? rows || [] : (rows || []).filter((r) => r.loan_type === loanType)), [rows, loanType]);
    const { months, purposes } = useMemo(() => {
        const monthSet = new Set();
        const purposeSet = new Set();
        safeRows.forEach((r) => {
            monthSet.add(r.month);
            purposeSet.add(r.purpose);
        });
        return { months: [...monthSet].sort(), purposes: [...purposeSet].sort() };
    }, [safeRows]);
    const colors = cyclePalette(CHART_COLORS.categorical, purposes.length);
    const data = useMemo(
        () => ({
            labels: months,
            datasets: purposes.map((purpose, i) => ({
                label: formatLegendText(purpose),
                backgroundColor: colors[i],
                data: months.map((m) => safeRows.find((r) => r.month === m && r.purpose === purpose)?.amount || 0),
            })),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- colors is a plain cyclePalette(...) call keyed off purposes.length, not its own memo
        [safeRows, months, purposes]
    );
    const options = useMemo(
        () => ({ ...BASE_OPTIONS, scales: { x: { stacked: true, ticks: { maxRotation: 90, minRotation: 90 } }, y: { stacked: true } }, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } } }),
        []
    );
    if (!safeRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Loan term vs borrower rate, one dataset per credit rating - `loans` is
// the loanbook endpoint's per-loan snapshot (~9k rows, one per loan).
// Establishes this repo's first `type: 'scatter'` Chart.js usage (none
// existed before - confirmed via a full-repo search), following the same
// plain-dataset-object convention every other chart in this file already
// uses rather than inventing a new one.
function LoanScatterChart({ loans, loanType }) {
    const safeLoans = useMemo(() => (loanType === 'All' ? loans || [] : (loans || []).filter((l) => l.type === loanType)), [loans, loanType]);
    const ratings = useMemo(() => [...new Set(safeLoans.map((l) => l.rating))].sort(), [safeLoans]);
    const colors = cyclePalette(CHART_COLORS.categorical, ratings.length);
    const data = useMemo(
        () => ({
            datasets: ratings.map((rating, i) => ({
                label: formatLegendText(rating),
                backgroundColor: colors[i],
                data: safeLoans.filter((l) => l.rating === rating).map((l) => ({ x: l.term, y: l.rate * 100 })),
            })),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- colors is a plain cyclePalette(...) call keyed off ratings.length, not its own memo
        [safeLoans, ratings]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: { x: { type: 'linear', title: { display: true, text: 'Term (months)' } }, y: { title: { display: true, text: 'Borrower Rate' }, ticks: { callback: (v) => `${v}%` } } },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    if (!safeLoans.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="scatter" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// 100%-stacked bar of repayment schedule type (amortising/bullet/linear) by
// vintage year, for one segment.
function RepaymentScheduleChart({ rows }) {
    const safeRows = rows || [];
    const cols = [
        { key: 'amortising', label: 'Amortising', color: '#077186' },
        { key: 'bullet', label: 'Bullet', color: CHART_COLORS.categorical[1] },
        { key: 'linear', label: 'Linear', color: '#8a8f98' },
    ].filter((c) => safeRows.some((row) => row[c.key] !== undefined));
    const data = useMemo(
        () => ({
            labels: safeRows.map((row) => row.year),
            datasets: cols.map((c) => ({ label: c.label, backgroundColor: c.color, data: safeRows.map((row) => pctToFloat(row[c.key])) })),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [safeRows]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: { x: { stacked: true }, y: { stacked: true, max: 100, ticks: { callback: (v) => `${v}%` } } },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    if (!safeRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Grouped bar of interest-rate-bucket distribution, one series per year -
// rows are already pivoted wide (one column per year).
function InterestDistributionYearChart({ rows }) {
    const safeRows = rows || [];
    const yearCols = safeRows.length ? Object.keys(safeRows[0]).filter((k) => k !== 'Interest_rate_category') : [];
    const colors = cyclePalette(CHART_COLORS.categorical, yearCols.length);
    const data = useMemo(
        () => ({
            labels: safeRows.map((row) => row.Interest_rate_category),
            datasets: yearCols.map((year, i) => ({ label: year, backgroundColor: colors[i], data: safeRows.map((row) => pctToFloat(row[year])) })),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [safeRows]
    );
    const options = useMemo(
        () => ({ ...BASE_OPTIONS, scales: { y: { ticks: { callback: (v) => `${v}%` } } }, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } } }),
        []
    );
    if (!safeRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Grouped bar of net default amount by credit rating, one series per
// repayment type.
function DefaultCreditScoreChart({ rows }) {
    const safeRows = rows || [];
    const byType = new Map();
    safeRows.forEach((row) => {
        if (!byType.has(row.repayment_type)) byType.set(row.repayment_type, new Map());
        byType.get(row.repayment_type).set(row.rating, row.net_default);
    });
    const ratings = [...new Set(safeRows.map((r) => r.rating))];
    const colors = { amortising: CHART_COLORS.categorical[1], bullet: '#077186', linear: '#8a8f98' };
    const data = useMemo(
        () => ({
            labels: ratings,
            datasets: [...byType.entries()].map(([type, byRating]) => ({
                label: type[0].toUpperCase() + type.slice(1),
                backgroundColor: colors[type] || '#8a8f98',
                data: ratings.map((r) => byRating.get(r) || 0),
            })),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [safeRows]
    );
    const options = useMemo(() => ({ ...BASE_OPTIONS, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } } }), []);
    if (!safeRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Repayment status by vintage, last 24 months - the exact table behind the
// original PowerBI relationship bug (monthly_current_amount is not a valid
// unique key; month_yr is). Uses the doc's own pre-computed "repaid"/
// "current"/"defaulted" percentage strings directly.
function RepaymentStatusChart({ rows }) {
    const safeRows = rows || [];
    const sorted = useMemo(() => [...safeRows].sort((a, b) => a.month_yr.localeCompare(b.month_yr)).slice(-24), [rows]); // eslint-disable-line react-hooks/exhaustive-deps -- safeRows is derived fresh from rows each render, listing rows avoids recomputing on every unrelated re-render
    const data = useMemo(
        () => ({
            labels: sorted.map((row) => row.month_yr),
            datasets: [
                { label: 'Repaid', backgroundColor: CHART_COLORS.categorical[1], data: sorted.map((row) => pctToFloat(row.repaid)) },
                { label: 'Current', backgroundColor: '#077186', data: sorted.map((row) => pctToFloat(row.current)) },
                { label: 'Defaulted', backgroundColor: '#c9a24b', data: sorted.map((row) => pctToFloat(row.defaulted)) },
            ],
        }),
        [sorted]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: { x: { stacked: true, ticks: { maxRotation: 90, minRotation: 90 } }, y: { stacked: true, max: 100, ticks: { callback: (v) => `${v}%` } } },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    if (!safeRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Arrears aging buckets over time, one segment at a time - lend_arrears (a
// separate small collection, merged into the doc as `doc.arrears` by
// fetchLendReportDoc) predates a bucket-schema change: older docs only have
// {report_date, Consumer/SME/Mortgage: {"1-30 days":.., "31-60 days":..,
// "61-90 days":..}}, newer ones add "91-120"/"121-150"/"151-180 days" plus
// "current"/"defaulted" state buckets - ARREARS_BUCKET_ORDER lists every
// bucket seen across the collection's history so older rows just render
// with the newer buckets at 0 rather than being dropped. The segment key
// itself changed too ("Consumer" -> "Privat" partway through) - normalized
// to the same 'private'/'sme'/'mortgage' keys SEGMENT_LABELS already uses.
const ARREARS_BUCKET_ORDER = ['current', '1-30 days', '31-60 days', '61-90 days', '91-120 days', '121-150 days', '151-180 days', 'defaulted'];
const ARREARS_BUCKET_COLORS_BASE = ['#077186', '#4a90c4', '#8ecae6', '#c9a24b', '#e0a458', '#d97b4f', '#b5495b'];
// "defaulted" (last bucket) is the shared navy accent (CHART_COLORS.
// categorical[1]) - a function, not a frozen array literal, since that
// value is remapped in dark mode (see chart_theme.js's applyChartColorsTheme)
// and this needs to read it fresh each time rather than freeze whatever it
// was at module-load. Not a memo dependency anywhere it's used, so a new
// array each call is fine.
function getArrearsBucketColors() {
    return [...ARREARS_BUCKET_COLORS_BASE, CHART_COLORS.categorical[1]];
}
const ARREARS_SEGMENT_KEYS = { private: ['Private', 'Privat', 'Consumer'], sme: ['SME'], mortgage: ['Mortgage'] };

function ArrearsChart({ rows, segment }) {
    const safeRows = rows || [];
    const segKeys = ARREARS_SEGMENT_KEYS[segment];
    const filtered = useMemo(() => safeRows.filter((r) => segKeys.some((k) => r[k])), [rows, segKeys]); // eslint-disable-line react-hooks/exhaustive-deps -- safeRows is derived fresh from rows each render
    const data = useMemo(
        () => {
            const arrearsBucketColors = getArrearsBucketColors();
            return {
                labels: filtered.map((r) => r.report_date),
                datasets: ARREARS_BUCKET_ORDER.map((bucket, i) => ({
                    label: formatLegendText(bucket),
                    backgroundColor: arrearsBucketColors[i],
                    data: filtered.map((r) => {
                        const segRow = segKeys.map((k) => r[k]).find((v) => v);
                        return (segRow?.[bucket] || 0) * 100;
                    }),
                })),
            };
        },
        [filtered, segKeys]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: { x: { stacked: true, ticks: { maxRotation: 90, minRotation: 90 } }, y: { stacked: true, max: 100, ticks: { callback: (v) => `${v}%` } } },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    if (!filtered.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function ArrearsSection({ doc }) {
    const [segment, setSegment] = useState('private');
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} />
            <ChartCard title={`Arrears Aging - ${SEGMENT_LABELS[segment]} Loans`} height={380}>
                <ArrearsChart rows={doc.arrears} segment={segment} />
            </ChartCard>
        </>
    );
}

// Cumulative portfolio performance for one segment - Before/After Fees
// (Index/Index_after_fees, base 1000 rebased to 1.000) on one chart. The
// sibling index_* doc pre-scaffolds rows years into the future - the far
// future ones use the literal string "NaN" in every numeric field (not a
// real null, filtered out the same way pdf_generation/_charts/lend.py's
// create_lend_cumulative_performance_figure does), but the nearer future
// (report date up to ~2033) is pre-scaffolded with real zeros instead,
// which the "is it a number" check alone doesn't catch - so this also
// truncates at reportMonth to stop the line exactly at the report date
// rather than continuing flat for years past it.
function CumulativePerformanceChart({ indexRows, reportMonth }) {
    const clean = useMemo(
        () => (indexRows || []).filter((r) => typeof r.Index === 'number' && typeof r.Index_after_fees === 'number' && (!reportMonth || r.year_month <= reportMonth)),
        [indexRows, reportMonth]
    );
    const data = useMemo(
        () => ({
            datasets: [
                { label: 'Before Fees', borderColor: '#077186', backgroundColor: '#077186', data: clean.map((r) => ({ x: r.year_month, y: r.Index / 1000 })), fill: false, pointRadius: 0 },
                { label: 'After Fees', borderColor: CHART_COLORS.categorical[1], backgroundColor: CHART_COLORS.categorical[1], data: clean.map((r) => ({ x: r.year_month, y: r.Index_after_fees / 1000 })), fill: false, pointRadius: 0 },
            ],
        }),
        [clean]
    );
    const options = useMemo(
        () => ({ ...BASE_OPTIONS, scales: { x: { type: 'category' } }, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } } }),
        []
    );
    if (!clean.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="line" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Default rate & return over time, from the same doc.index data
// CumulativePerformanceChart reads - the PBIX's Performance Metrics page
// pairs this small chart with the Risk Metrics table (RiskMetricsSection).
// (The dashboard previously had a separate "Index Analytics" tab with a
// dropdown to explore every field in this doc - removed per direct
// feedback against the live PBIX, which only shows the Cumulative
// Performance Index here with no such metric picker.)
function DefaultRateReturnChart({ rows, reportMonth }) {
    const safeRows = rows || [];
    const clean = useMemo(
        () => safeRows.filter((r) => typeof r.DefaultRate === 'number' && typeof r.Return === 'number' && (!reportMonth || r.year_month <= reportMonth)),
        [rows, reportMonth] // eslint-disable-line react-hooks/exhaustive-deps -- safeRows is derived fresh from rows each render; the sibling index_* doc pre-scaffolds future rows (some as the literal string "NaN", others as real zeros) - see CumulativePerformanceChart's identical truncation above
    );
    const data = useMemo(
        () => ({
            labels: clean.map((r) => r.year_month),
            datasets: [
                { type: 'bar', label: 'Default Rate', backgroundColor: '#c9a24b', data: clean.map((r) => r.DefaultRate * 100), yAxisID: 'y' },
                { type: 'line', label: 'Return', borderColor: '#077186', backgroundColor: '#077186', data: clean.map((r) => r.Return * 100), yAxisID: 'y1', fill: false, pointRadius: 0 },
            ],
        }),
        [clean]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: {
                x: { ticks: { maxRotation: 90, minRotation: 90 } },
                y: { position: 'left', ticks: { callback: (v) => `${v}%` } },
                y1: { position: 'right', ticks: { callback: (v) => `${v}%` }, grid: { drawOnChartArea: false } },
            },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    if (!clean.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Projected cashflow of outstanding loans: stacked bar (Repayments,
// Interest) + secondary-axis line (remaining outstanding volume, as % of
// the first period's balance). Mortgage's rows have no OTC-split interest
// columns (no OTC trades at all for that segment) - falls back to the
// unsplit column, same as the PDF-side chart builder.
function ProjectedCashflowChart({ rows }) {
    const safeRows = rows || [];
    const data = useMemo(() => {
        if (!safeRows.length) return { labels: [], datasets: [] };
        const first = safeRows[0].Outstanding_temp;
        const repayments = safeRows.map((r) =>
            'planned_principal_payment_no_OTC' in r ? r.planned_principal_payment_no_OTC + (r.planned_principal_payment_OTC || 0) : r.planned_principal_payment
        );
        const interest = safeRows.map((r) =>
            'planned_interest_payment_no_OTC' in r ? r.planned_interest_payment_no_OTC + (r.planned_interest_payment_OTC || 0) : r.planned_interest_payment
        );
        const outstandingPct = safeRows.map((r) => (r.Outstanding_temp / first) * 100);
        return {
            labels: safeRows.map((r) => r.Date),
            datasets: [
                { type: 'bar', label: 'Repayments', backgroundColor: '#077186', data: repayments, stack: 'cf', yAxisID: 'y' },
                { type: 'bar', label: 'Interest', backgroundColor: CHART_COLORS.categorical[1], data: interest, stack: 'cf', yAxisID: 'y' },
                { type: 'line', label: 'Remaining outstanding volume in %', borderColor: '#8a8f98', backgroundColor: '#8a8f98', data: outstandingPct, yAxisID: 'y1', fill: false, pointRadius: 0 },
            ],
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: {
                x: { ticks: { maxRotation: 90, minRotation: 90 } },
                y: { position: 'left' },
                y1: { position: 'right', min: 0, max: 100, ticks: { callback: (v) => `${v}%` }, grid: { drawOnChartArea: false } },
            },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    if (!safeRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// 12-month rolling returns, grouped bar of Return vs Return-after-fees.
function RollingReturnsChart({ rows, afterFeesRows }) {
    const safeRows = rows || [];
    const data = useMemo(
        () => ({
            labels: safeRows.map((r) => r.Date),
            datasets: [
                { label: 'Return', backgroundColor: '#077186', data: safeRows.map((r) => r.Return12months * 100) },
                ...(afterFeesRows?.length
                    ? [{ label: 'Return - After Fees', backgroundColor: CHART_COLORS.categorical[1], data: afterFeesRows.map((r) => r.Return12months * 100) }]
                    : []),
            ],
        }),
        [rows, afterFeesRows] // eslint-disable-line react-hooks/exhaustive-deps -- safeRows is derived fresh from rows each render
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: { x: { ticks: { maxRotation: 90, minRotation: 90 } }, y: { ticks: { callback: (v) => `${v}%` } } },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    if (!safeRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// Floor-match: the band whose cutoff is the largest one <= ratePct (bands
// are contiguous and ascending in severity, so this is exact - no
// interpolation or nearest-neighbor guessing involved). `tables` is the
// live-fetched { sp: [...], fitch: [...] } from GET /lend-loanbook/rating-tables
// (backend/api/lend_loanbook.js) - each a MIN(DefaultRate) per (Rating,
// Year_horizon) band, aggregated straight from the Analytics DB's `sp`/`fitch`
// collections (the same source of truth a now-deleted static snapshot,
// lend_rating_tables.js, used to hand-compress and could silently drift out
// of sync with).
function findRatingBand(tables, agency, horizon, ratePct) {
    const table = (agency === 'sp' ? tables?.sp : tables?.fitch) || [];
    const bands = table.filter((b) => b.horizon === horizon).sort((a, b) => a.cutoff - b.cutoff);
    let match = bands[0];
    for (const band of bands) {
        if (band.cutoff <= ratePct) match = band;
        else break;
    }
    return match?.rating ?? null;
}

// Indicative Credit Rating Tool - the PBIX's sole non-hidden "Credit Rating"
// page (several duplicate/dev "S&P"/"Fitch" pages also exist in the PBIX but
// are hidden from its tab bar - confirmed via a live screenshot of the app,
// so not used as a spec here). Looks up an agency's idealized default-rate
// table (fetched live - see findRatingBand above) for the selected
// Year_horizon, against a freely-adjustable override rate - mirrors the
// PBIX's own "Parameter 2" slicer, which is independent of the LoanType
// selection (confirmed against the same screenshot: Private showed a real
// Default Rate of 2.5% while the override slider sat at 6.4%, unrelated to
// Private's own number - the slider doesn't resync when the segment
// changes, only seeds its initial value from whichever segment is selected
// first).
//
// This is a new calculation, not a passthrough of an existing report field -
// flag for compliance review before this is shown to Originator customers,
// same as any other new metric introduced into a Platform Report.
function IndicativeCreditRatingSection({ doc, ratingTables, ratingTablesLoading }) {
    const walRows = doc.WAL_table || [];
    const loanTypes = walRows.map((r) => r.LoanType);
    const [loanType, setLoanType] = useState(loanTypes[0] || null);
    // Sorted ascending by year count, not DB/insertion order - AQL's COLLECT
    // gives no ordering guarantee. All horizons follow the fixed "N year(s)"
    // shape (see lend_loanbook.js), so a plain numeric-prefix sort is exact.
    const yearHorizons = useMemo(
        () => [...new Set((ratingTables?.sp || []).map((b) => b.horizon))].sort((a, b) => parseInt(a, 10) - parseInt(b, 10)),
        [ratingTables]
    );
    const [horizon, setHorizon] = useState(null);
    useEffect(() => {
        if (yearHorizons.length && horizon === null) setHorizon(yearHorizons[0]);
    }, [yearHorizons, horizon]);
    const [agency, setAgency] = useState('sp');
    const [overrideRate, setOverrideRate] = useState(() => {
        const first = walRows[0];
        return first ? Math.round(first.DefaultRate * 10000) / 100 : 0;
    });

    if (!walRows.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    if (ratingTablesLoading || !horizon) return <LoadingState label="Loading rating tables" />;

    const selectedRow = walRows.find((r) => r.LoanType === loanType);
    const rating = findRatingBand(ratingTables, agency, horizon, overrideRate);

    return (
        <div className="grid">
            <div className="col-12 md:col-6">
                <div className="flex align-items-center gap-3 mb-3">
                    <span className="font-bold">Loan Type</span>
                    <Dropdown value={loanType} options={loanTypes.map((l) => ({ label: l, value: l }))} onChange={(e) => setLoanType(e.value)} />
                </div>
                <div className="pr-card mb-3 flex gap-5">
                    <div>
                        <div className="pr-kpi-label">Default Rate</div>
                        <div className="pr-kpi-value pr-kpi-value-lg">{selectedRow ? `${(selectedRow.DefaultRate * 100).toFixed(1)}%` : '-'}</div>
                    </div>
                    <div>
                        <div className="pr-kpi-label">Weighted Average Life (in months)</div>
                        <div className="pr-kpi-value pr-kpi-value-lg">{selectedRow ? selectedRow.WAL.toFixed(2) : '-'}</div>
                    </div>
                </div>
                <SelectButton
                    value={horizon}
                    options={yearHorizons.map((h) => ({ label: h, value: h }))}
                    onChange={(e) => (e.value ? setHorizon(e.value) : null)}
                    className="mb-3"
                />
                <div className="pr-card">
                    <div className="font-bold mb-2">Default Rate (in %)</div>
                    <InputNumber
                        value={overrideRate}
                        onValueChange={(e) => setOverrideRate(e.value ?? 0)}
                        mode="decimal"
                        minFractionDigits={1}
                        maxFractionDigits={2}
                        min={0}
                        max={100}
                        className="mb-2"
                    />
                    <Slider value={overrideRate} onChange={(e) => setOverrideRate(e.value)} min={0} max={100} step={0.01} />
                </div>
            </div>
            <div className="col-12 md:col-6">
                <div className="pr-card">
                    <div className="flex gap-4 mb-3">
                        <div className="flex align-items-center gap-2">
                            <RadioButton inputId="lend-rating-agency-sp" checked={agency === 'sp'} onChange={() => setAgency('sp')} />
                            <label htmlFor="lend-rating-agency-sp">S&amp;P</label>
                        </div>
                        <div className="flex align-items-center gap-2">
                            <RadioButton inputId="lend-rating-agency-fitch" checked={agency === 'fitch'} onChange={() => setAgency('fitch')} />
                            <label htmlFor="lend-rating-agency-fitch">Fitch</label>
                        </div>
                    </div>
                    <h5 className="pr-card-title">Indicative Credit Rating Tool</h5>
                    <div className="flex align-items-baseline gap-3">
                        <span className="text-color-secondary">Indicative Rating:</span>
                        <span className="text-5xl font-bold" style={{ color: SEGMENT_COLORS.private }}>
                            {rating || '-'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Sections needing a segment toggle - small wrapper components so each tab's
// body owns its own toggle state independently.
// ============================================================================

function RepaymentScheduleSection({ doc }) {
    const [segment, setSegment] = useState('private');
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} />
            <ChartCard title={`Repayment Schedules by Vintage - ${SEGMENT_LABELS[segment]} Loans`} height={340}>
                <RepaymentScheduleChart rows={doc[`${segment}_repayment_schedules`]} />
            </ChartCard>
        </>
    );
}

// Maps this file's existing 'private'/'sme'/'mortgage' segment keys (used
// by <SegmentToggle> everywhere else) to lend_format_for_index's own raw
// loan_type strings ('Privat'/'SME'/'Mortgage') - needed wherever a
// SegmentToggle-driven tab also filters the /lend-loanbook endpoint's data.
const SEGMENT_TO_LOAN_TYPE = { private: 'Privat', sme: 'SME', mortgage: 'Mortgage', total: 'All' };

function InterestDistributionSection({ doc, loanbook }) {
    const [segment, setSegment] = useState('private');
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} />
            <ChartCard title={`Interest Rate Distribution - ${SEGMENT_LABELS[segment]} Loans`} height={340}>
                <InterestDistributionYearChart rows={doc[`${segment}_interest_dis`]} />
            </ChartCard>
            <ChartCard title={`Loan Term vs Borrower Rate, by Credit Rating - ${SEGMENT_LABELS[segment]} Loans`} height={380}>
                <LoanScatterChart loans={loanbook?.loans} loanType={SEGMENT_TO_LOAN_TYPE[segment]} />
            </ChartCard>
        </>
    );
}

// Loanbook Overview (I & II in the PBIX, combined here) - the charts that
// need per-loan granularity lend_performance_report_2 doesn't have, backed
// by the new /lend-loanbook endpoint (see LendDocTabs' fetch below). The
// PBIX's own origination-by-date stacked combo on this page is the same
// origination volume already shown on the "Origination Volume" tab (that
// one reads the pre-aggregated `loan_origination` field rather than the
// new endpoint) - not repeated here to avoid showing the same numbers
// twice under two tabs. Every chart here carries its own loan_type filter,
// matching the PBIX's own Lend_mapped.loan_type/Key1.2 slicers - driven by
// the same <SegmentToggle> every other section uses (previously its own
// separate loanType/LOANBOOK_TYPE_OPTIONS toggle - translated via
// SEGMENT_TO_LOAN_TYPE, same as InterestDistributionSection already does).
function LoanbookOverviewSection({ loanbook, loanbookLoading }) {
    const [segment, setSegment] = useState('total');
    const loanType = SEGMENT_TO_LOAN_TYPE[segment];
    if (loanbookLoading) return <LoadingState label="Loading loanbook" />;
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} includeTotal />
            <ChartCard title={`Borrower Rate Distribution by Month - ${SEGMENT_LABELS[segment]} Loans`} height={340}>
                <BorrowerRateBoxPlotChart rows={loanbook?.rate_stats_by_month} loanType={loanType} />
            </ChartCard>
            <div className="grid">
                <div className="col-12 md:col-5">
                    <ChartCard title="Origination Volume by Purpose" height={360}>
                        <OriginationByPurposePieChart rows={loanbook?.origination_by_purpose} loanType={loanType} />
                    </ChartCard>
                </div>
                <div className="col-12 md:col-7">
                    <ChartCard title="Origination Volume by Purpose and Month" height={360}>
                        <OriginationByPurposeColumnChart rows={loanbook?.origination_by_purpose} loanType={loanType} />
                    </ChartCard>
                </div>
            </div>
        </>
    );
}

function DefaultCreditScoreSection({ doc }) {
    const [segment, setSegment] = useState('private');
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} />
            <ChartCard title={`Default Amounts Per Credit Score - ${SEGMENT_LABELS[segment]} Loans`} height={340}>
                <DefaultCreditScoreChart rows={doc[`${segment}_default_credit_score`]} />
            </ChartCard>
        </>
    );
}

const VINTAGE_DOC_KEY = { private: 'consumer', sme: 'sme', mortgage: 'mortgage' };

// Credit-rating-dimensioned vintage curve. Reproduces the PBIX's actual DAX
// (obtained directly from the user): vintage_metric[Metric] is
// "Default"/"Recovery"/"Loss", and Dynamic Rate = Dynamic Amount / Dynamic
// Denominator, where the denominator SWITCHes per metric - "Default" and
// "Loss" divide by [Origination] (the cohort's fixed total), but "Recovery"
// divides by cumulative [Default], not Origination.
//
// Source data is `doc.vintage.nominal_by_type_rating` (see
// generate_platform_report.js's fetchLendReportDoc, which already merges the
// vintage_* sibling doc in as `doc.vintage` - no separate fetch needed here).
//
// Both the max_month cutoff and the forward-fill-before-summing step below
// are taken directly from the actual Power Query M behind the PBIX's own
// vintage_data table (given to us verbatim by the user), not reconstructed:
// max_month = (report_year - vintage_year) * 12 + report_month (no "-1" - an
// earlier version of this file had that off by one month). The forward-fill
// matters because each (vintage_year, credit_rating) slice of
// nominal_by_type_rating can stop many months before the cohort's real
// max_month - confirmed directly against the DB: Mortgage/2019's own rating
// buckets end at month 28 (A+), 43 (A), 47 (C) and 85 (B), not a shared ~92 -
// so summing only the rows that literally exist at a given month (as an
// earlier version of this chart did) silently drops a rating once its own
// array ends, shrinking the denominator and inflating the combined rate for
// later months. That was the cause of the "All ratings" view not lining up
// with the existing (single-line-per-year, already-trusted) Default Rates
// per Vintage tab reported against this chart.
const LOAN_TYPE_LABELS = { All: 'All Loans', Privat: 'Private', SME: 'SME', Mortgage: 'Mortgage' };
const VINTAGE_METRIC_AMOUNT = { Default: (r) => r.nominal_default, Recovery: (r) => r.nominal_recovery, Loss: (r) => r.nominal_default - r.nominal_recovery };
const VINTAGE_METRIC_DENOMINATOR = { Default: (r) => r.origination_volume, Recovery: (r) => r.nominal_default, Loss: (r) => r.origination_volume };

function maxRealMonth(vintageYear, reportDate) {
    if (!reportDate) return Infinity;
    const [reportYear, reportMonthNum] = reportDate.split('-').map(Number);
    return (reportYear - vintageYear) * 12 + reportMonthNum;
}

// Forward-fills each slice's row-series up to its own max_month, then sums
// the given slices together month by month - the same two-step shape as the
// PBIX's Table.Group -> expand-to-a-shared-month-axis -> FillDown -> sum. A
// slice with no rows at all (a rating/year the cohort never used) is skipped
// throughout rather than treated as zero, so it can't pull a combined rate
// toward zero.
function sumForwardFilledSlices(slices) {
    const list = slices.filter((s) => s.rows.length);
    if (!list.length) return [];
    const globalMax = Math.max(...list.map((s) => s.maxMonth));
    const pointers = list.map(() => 0);
    const points = [];
    for (let m = 0; m <= globalMax; m++) {
        let sumDefault = 0;
        let sumRecovery = 0;
        let sumOrig = 0;
        let any = false;
        list.forEach((s, i) => {
            if (m > s.maxMonth) return;
            while (pointers[i] + 1 < s.rows.length && s.rows[pointers[i] + 1].month <= m) pointers[i] += 1;
            const row = s.rows[pointers[i]];
            if (row && row.month <= m) {
                any = true;
                sumDefault += row.nominal_default;
                sumRecovery += row.nominal_recovery;
                sumOrig += row.origination_volume;
            }
        });
        if (any) points.push({ month: m, nominal_default: sumDefault, nominal_recovery: sumRecovery, origination_volume: sumOrig });
    }
    return points;
}

// One line per `legendValues` entry, each summing every value in
// `includeValues` (the other dimension's selection) together via
// sumForwardFilledSlices above - `legendBy` picks which of vintage_year/
// credit_rating is the line-per-value ("legend") dimension and which is the
// summed-together dimension, per the user's ask for one or the other, not
// the previous fixed cartesian year-x-rating-per-line scheme. Combining more
// than one rating or year into a single line is genuinely new arithmetic (a
// forward-filled sum) - a single rating with a single vintage year stays an
// exact, unmodified passthrough of nominal_by_type_rating either way.
function buildVintageLines(rows, loanType, legendBy, legendValues, includeValues, reportDate) {
    return legendValues.map((legendValue) => {
        const slices = includeValues.map((includeValue) => {
            const vintageYear = legendBy === 'year' ? legendValue : includeValue;
            const creditRating = legendBy === 'year' ? includeValue : legendValue;
            return {
                maxMonth: maxRealMonth(vintageYear, reportDate),
                rows: rows
                    .filter((r) => r.loan_type === loanType && r.vintage_year === vintageYear && r.credit_rating === creditRating)
                    .sort((a, b) => a.month - b.month),
            };
        });
        return { label: formatLegendText(String(legendValue)), points: sumForwardFilledSlices(slices) };
    });
}

function VintageByCreditRatingChart({ rows, loanType, legendBy, legendValues, includeValues, metric, reportDate }) {
    const safeRows = rows || [];
    const amountFn = VINTAGE_METRIC_AMOUNT[metric];
    const denomFn = VINTAGE_METRIC_DENOMINATOR[metric];
    const lines = useMemo(
        () => buildVintageLines(safeRows, loanType, legendBy, legendValues, includeValues, reportDate).filter((l) => l.points.length),
        [rows, loanType, legendBy, legendValues, includeValues, reportDate] // eslint-disable-line react-hooks/exhaustive-deps -- safeRows is derived fresh from rows each render
    );
    const colors = cyclePalette(CHART_COLORS.categorical, lines.length);
    const data = useMemo(
        () => ({
            datasets: lines.map((line, i) => ({
                label: line.label,
                borderColor: colors[i],
                backgroundColor: colors[i],
                data: line.points.map((r) => {
                    const denom = denomFn(r);
                    return { x: r.month, y: denom ? (amountFn(r) / denom) * 100 : null };
                }),
                fill: false,
                pointRadius: 0,
            })),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [lines, amountFn, denomFn]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: { x: { type: 'linear', title: { display: true, text: 'Months since origination' } }, y: { ticks: { callback: (v) => `${v}%` } } },
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        }),
        []
    );
    if (!lines.length) return <EmptyState icon="pi-filter-slash" title="No data for this selection" compact />;
    return <Chart key={chartRemountKey(data)} type="line" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

const EMPTY_ROWS = [];

function VintageByCreditRatingSection({ doc }) {
    const rows = doc.vintage?.nominal_by_type_rating || EMPTY_ROWS;
    const loanTypes = useMemo(() => [...new Set(rows.map((r) => r.loan_type))].sort(), [rows]);
    const [loanType, setLoanType] = useState(null);
    const effectiveLoanType = loanTypes.includes(loanType) ? loanType : loanTypes[0];
    // Defaults to Loss (net-of-recovery default rate) + legend by Vintage
    // Year, matching the old "Default Rates per Vintage" tab's own default
    // view (this tab now replaces it) - switch either control to see the
    // credit-rating breakdown.
    const [metric, setMetric] = useState('Loss');
    const [legendBy, setLegendBy] = useState('year');

    const allRatings = useMemo(() => [...new Set(rows.filter((r) => r.loan_type === effectiveLoanType).map((r) => r.credit_rating))].sort(), [rows, effectiveLoanType]);
    const allYears = useMemo(() => [...new Set(rows.filter((r) => r.loan_type === effectiveLoanType).map((r) => r.vintage_year))].sort(), [rows, effectiveLoanType]);
    const [ratings, setRatings] = useState(null);
    const [years, setYears] = useState(null);
    const effectiveRatings = ratings || allRatings;
    const effectiveYears = years || allYears;

    if (!loanTypes.length) return <EmptyState icon="pi-inbox" title="No data" compact />;

    const legendValues = legendBy === 'year' ? effectiveYears : effectiveRatings;
    const includeValues = legendBy === 'year' ? effectiveRatings : effectiveYears;

    return (
        <>
            <div className="flex gap-3 mb-3 align-items-center flex-wrap">
                <SelectButton value={effectiveLoanType} options={loanTypes.map((t) => ({ label: LOAN_TYPE_LABELS[t] || t, value: t }))} onChange={(e) => (e.value ? setLoanType(e.value) : null)} />
                <SelectButton value={metric} options={Object.keys(VINTAGE_METRIC_AMOUNT).map((m) => ({ label: m, value: m }))} onChange={(e) => (e.value ? setMetric(e.value) : null)} />
                <div className="flex align-items-center gap-2">
                    <span className="text-color-secondary text-sm">Legend</span>
                    <SelectButton
                        value={legendBy}
                        options={[
                            { label: 'Vintage Year', value: 'year' },
                            { label: 'Credit Rating', value: 'rating' },
                        ]}
                        onChange={(e) => (e.value ? setLegendBy(e.value) : null)}
                    />
                </div>
                <MultiSelect value={effectiveRatings} options={allRatings.map((r) => ({ label: r, value: r }))} onChange={(e) => setRatings(e.value)} placeholder="Credit Rating" display="chip" style={{ minWidth: '14rem' }} />
                <MultiSelect value={effectiveYears} options={allYears.map((y) => ({ label: String(y), value: y }))} onChange={(e) => setYears(e.value)} placeholder="Vintage Year" display="chip" style={{ minWidth: '14rem' }} />
            </div>
            <ChartCard title={`${metric} Rate per Vintage by Credit Rating - ${LOAN_TYPE_LABELS[effectiveLoanType] || effectiveLoanType} Loans`} height={400}>
                <VintageByCreditRatingChart
                    rows={rows}
                    loanType={effectiveLoanType}
                    legendBy={legendBy}
                    legendValues={legendValues}
                    includeValues={includeValues}
                    metric={metric}
                    reportDate={doc.report_date}
                />
            </ChartCard>
        </>
    );
}

function RepaymentStatusSection({ doc }) {
    const [segment, setSegment] = useState('private');
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} />
            <ChartCard title={`Repayment Status by Vintage last 24 Months - ${SEGMENT_LABELS[segment]} Loans`} height={380}>
                <RepaymentStatusChart rows={doc[`repayment_status_${segment}`]} />
            </ChartCard>
        </>
    );
}

function RiskMetricsSection({ doc }) {
    const [segment, setSegment] = useState('private');
    const [afterFees, setAfterFees] = useState(false);
    const field = afterFees ? `risk_df_after_fees_${segment}` : `risk_df_${segment}`;
    return (
        <>
            <div className="flex gap-2 mb-3 align-items-center flex-wrap">
                <SegmentToggle value={segment} onChange={setSegment} includeTotal />
                <SelectButton
                    value={afterFees}
                    options={[{ label: 'Before Fees', value: false }, { label: 'After Fees', value: true }]}
                    onChange={(e) => (e.value !== null ? setAfterFees(e.value) : null)}
                />
            </div>
            <MatrixTable rows={doc[field]} labelCol="Parameters" labelHeader={`${SEGMENT_LABELS[segment]} Loans`} />
            <ChartCard title={`Default Rate & Return - ${SEGMENT_LABELS[segment]} Loans`} height={340}>
                <DefaultRateReturnChart rows={doc.index?.[VINTAGE_DOC_KEY[segment] || 'overall']} reportMonth={doc.report_date?.slice(0, 7)} />
            </ChartCard>
        </>
    );
}

function ReturnMatrixSection({ doc }) {
    const [segment, setSegment] = useState('private');
    const [afterFees, setAfterFees] = useState(false);
    const field = afterFees ? `monthly_return_matrix_after_fees_${segment}` : `monthly_return_matrix_${segment}`;
    return (
        <>
            <div className="flex gap-2 mb-3 align-items-center flex-wrap">
                <SegmentToggle value={segment} onChange={setSegment} includeTotal />
                <SelectButton
                    value={afterFees}
                    options={[{ label: 'Before Fees', value: false }, { label: 'After Fees', value: true }]}
                    onChange={(e) => (e.value !== null ? setAfterFees(e.value) : null)}
                />
            </div>
            <MatrixTable rows={doc[field]} labelCol="Year" labelHeader={SEGMENT_LABELS[segment]} />
        </>
    );
}

function CumulativePerformanceSection({ doc }) {
    const [segment, setSegment] = useState('private');
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} />
            <ChartCard title={`Cumulative Portfolio Performance: ${SEGMENT_LABELS[segment]} Loans`} height={340}>
                <CumulativePerformanceChart indexRows={doc.index?.[VINTAGE_DOC_KEY[segment]]} reportMonth={doc.report_date?.slice(0, 7)} />
            </ChartCard>
        </>
    );
}

function ProjectedCashflowSection({ doc }) {
    const [segment, setSegment] = useState('private');
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} />
            <ChartCard title={`Projected Cashflow of Outstanding Loans - ${SEGMENT_LABELS[segment]}`} height={360}>
                <ProjectedCashflowChart rows={doc[`${segment}_projected_cash_flow`]} />
            </ChartCard>
        </>
    );
}

function RollingReturnsSection({ doc }) {
    const [segment, setSegment] = useState('private');
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} />
            <ChartCard title={`12 Month Rolling Returns - ${SEGMENT_LABELS[segment]} Loans`} height={340}>
                <RollingReturnsChart rows={doc[`year_rolling_returns_${segment}`]} afterFeesRows={doc[`year_rolling_returns_after_fees_${segment}`]} />
            </ChartCard>
        </>
    );
}

// ============================================================================
// Tab assembly
// ============================================================================

// Hoisted to module scope (rather than inline array literals in the JSX
// below) so their reference stays stable across re-renders - PlatformReport's
// dashboard-highlight carousels re-render this whole tree every 9s on their
// own unrelated timer, and an inline literal here would count as a "changed"
// seriesFields prop on every one of those, busting GroupedSegmentBarChart's
// useMemo and handing Chart.js a new `data` object each time - which
// re-triggers its bar entrance animation even though nothing actually
// changed.
// `get color()` (not a plain `color: SEGMENT_COLORS.sme` property) is
// deliberate - a plain property would copy today's string value once, at
// module-load time, into this array and never see applyLendColorsTheme's
// later in-place mutation of SEGMENT_COLORS at all (the exact bug this
// fixes: "SME" series staying navy-on-navy-invisible in dark mode
// regardless of theme). A getter re-reads SEGMENT_COLORS on every access
// instead, while the array's own object identity - the thing the comment
// above this is actually protecting - never changes.
const CREDIT_SCORE_SERIES = [
    { key: 'outstanding_Consumer', label: 'Private', get color() { return SEGMENT_COLORS.private; } },
    { key: 'outstanding_SME', label: 'SME', get color() { return SEGMENT_COLORS.sme; } },
    { key: 'outstanding', label: 'Mortgage', get color() { return SEGMENT_COLORS.mortgage; } },
];
const TICKET_SIZE_SERIES = [
    { key: 'loan_original_amount_SME', label: 'SME', get color() { return SEGMENT_COLORS.sme; } },
    { key: 'loan_original_amount_Consumer', label: 'Private', get color() { return SEGMENT_COLORS.private; } },
    { key: 'loan_original_amount_Mortgage', label: 'Mortgage', get color() { return SEGMENT_COLORS.mortgage; } },
];

const ORIGINATION_SERIES = [
    { key: 'loan_original_amount_SME_no_OTC', label: 'SME', get color() { return SEGMENT_COLORS.sme; } },
    { key: 'loan_original_amount_Consumer_no_OTC', label: 'Private', get color() { return SEGMENT_COLORS.private; } },
    { key: 'loan_original_amount_Mortgage_no_OTC', label: 'Mortgage', get color() { return SEGMENT_COLORS.mortgage; } },
    { key: 'loan_original_amount_SME_OTC', label: 'OTC Trade - SME', color: '#8ecae6' },
    { key: 'loan_original_amount_Consumer_OTC', label: 'OTC Trade - Private', color: '#4a90c4' },
];

function aggregateOriginationByYear(loanOrigination) {
    const byYear = new Map();
    (loanOrigination || []).forEach((row) => {
        const year = row.year_month.slice(0, 4);
        if (!byYear.has(year)) byYear.set(year, { year_month: year });
        const acc = byYear.get(year);
        ORIGINATION_SERIES.forEach((s) => {
            acc[s.key] = (acc[s.key] || 0) + (row[s.key] || 0);
        });
    });
    return [...byYear.values()].sort((a, b) => a.year_month.localeCompare(b.year_month));
}

export function buildTabsForLendDoc(doc, loanbook, loanbookLoading, ratingTables, ratingTablesLoading) {
    if (!doc) return [];
    return [
        {
            key: 'loanbook_overview',
            header: 'Loanbook Overview',
            content: <LoanbookOverviewSection loanbook={loanbook} loanbookLoading={loanbookLoading} />,
        },
        {
            key: 'overview',
            header: 'General Overview',
            content: (
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <div className="pr-card h-full">
                            <h5 className="pr-card-title">Lend Private</h5>
                            <OverviewTable rows={doc.general_overview_final_private} />
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="pr-card h-full">
                            <h5 className="pr-card-title">Lend SME</h5>
                            <OverviewTable rows={doc.general_overview_final_sme} />
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="pr-card h-full">
                            <h5 className="pr-card-title">Lend Mortgage</h5>
                            <OverviewTable rows={doc.general_overview_final_mortgage} />
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="pr-card h-full">
                            <h5 className="pr-card-title">Lend Total</h5>
                            <OverviewTable rows={doc.general_overview_final_total} />
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'origination',
            header: 'Origination Volume',
            content: (
                <>
                    <ChartCard title="Origination Volume by Year" height={360}>
                        <StackedBarChart rows={aggregateOriginationByYear(doc.loan_origination)} series={ORIGINATION_SERIES} xField="year_month" />
                    </ChartCard>
                    <ChartCard title="Origination Volume by Month" height={380}>
                        <StackedBarChart rows={(doc.loan_origination || []).slice(-84)} series={ORIGINATION_SERIES} xField="year_month" />
                    </ChartCard>
                </>
            ),
        },
        {
            key: 'rates_score_ticket',
            header: 'Interest Rates & Credit Score',
            content: (
                <>
                    <ChartCard title="Average Interest Rates of Monthly Loan Origination Vintages" height={340}>
                        <InterestRateVintageChart indexDoc={doc.index} />
                    </ChartCard>
                    <ChartCard title="Outstanding Loans by Credit Score" height={340}>
                        <GroupedSegmentBarChart
                            rows={doc.outstanding_credit_score || []}
                            xField="source_platform_credit_rating"
                            seriesFields={CREDIT_SCORE_SERIES}
                        />
                    </ChartCard>
                    <ChartCard title="Average Ticket Size" height={340}>
                        <GroupedSegmentBarChart
                            rows={doc.average_ticket_size || []}
                            xField="year"
                            seriesFields={TICKET_SIZE_SERIES}
                        />
                    </ChartCard>
                </>
            ),
        },
        { key: 'repayment_schedules', header: 'Repayment Schedules', content: <RepaymentScheduleSection doc={doc} /> },
        {
            key: 'interest_distribution',
            header: 'Interest Rate Distribution',
            content: <InterestDistributionSection doc={doc} loanbook={loanbook} />,
        },
        { key: 'default_credit_score', header: 'Default per Credit Score', content: <DefaultCreditScoreSection doc={doc} /> },
        // Replaces the old single-line-per-year "Default Rates per Vintage"
        // tab (doc.vintage.mortgage/consumer/sme, net-of-recovery default
        // rate) - Legend: Vintage Year + metric: Loss + every credit rating
        // selected reproduces that old tab's numbers exactly (verified
        // directly against the DB across 5 vintage years, zero mismatches),
        // while also offering the credit-rating breakdown the old tab never
        // had.
        {
            key: 'vintage_by_rating',
            header: 'Vintage by Credit Rating',
            content: <VintageByCreditRatingSection doc={doc} />,
        },
        {
            key: 'credit_rating',
            header: 'Credit Rating',
            content: <IndicativeCreditRatingSection doc={doc} ratingTables={ratingTables} ratingTablesLoading={ratingTablesLoading} />,
        },
        {
            key: 'coupons_portfolio_stats',
            header: 'Coupons & Portfolio Stats',
            content: (
                <>
                    <h5 className="pr-subheading mb-2">Private Loans</h5>
                    <MatrixTable rows={doc.coupons_default_private} labelCol="year" labelHeader="Vintage" />
                    <h5 className="pr-subheading mb-2">SME Loans</h5>
                    <MatrixTable rows={doc.coupons_default_sme} labelCol="year" labelHeader="Vintage" />
                    <h5 className="pr-subheading mb-2">Mortgage Loans</h5>
                    <MatrixTable rows={doc.coupons_default_mortgage} labelCol="year" labelHeader="Vintage" />
                    <h5 className="pr-subheading mb-2">Selected Active Portfolio Statistics since Launch</h5>
                    <PortfolioStatsTable
                        statsBySegment={{
                            Private: doc.private_active_portfolio_stats,
                            SME: doc.sme_active_portfolio_stats,
                            Mortgage: doc.mortgage_active_portfolio_stats,
                            'All Loans': doc.overall_active_portfolio_stats,
                        }}
                    />
                </>
            ),
        },
        { key: 'arrears', header: 'Arrears', content: <ArrearsSection doc={doc} /> },
        { key: 'repayment_status', header: 'Repayment Status', content: <RepaymentStatusSection doc={doc} /> },
        { key: 'risk_metrics', header: 'Performance & Risk Metrics', content: <RiskMetricsSection doc={doc} /> },
        { key: 'return_matrix', header: 'Monthly Return Matrix', content: <ReturnMatrixSection doc={doc} /> },
        { key: 'cumulative_performance', header: 'Index Analytics', content: <CumulativePerformanceSection doc={doc} /> },
        { key: 'projected_cashflow', header: 'Projected Cashflow', content: <ProjectedCashflowSection doc={doc} /> },
        { key: 'rolling_returns', header: 'Rolling Returns', content: <RollingReturnsSection doc={doc} /> },
    ];
}

// Default export: the full tab set with its own active-tab state, same
// lazy-render pattern as ReportDocTabs (PlatformReport.js). Also owns the
// one fetch to the new /lend-loanbook endpoint (backend/api/lend_loanbook.js)
// - a single request per report_date, cached here for every tab that needs
// per-loan data (Loanbook Overview, the Interest Rate Distribution scatter),
// same "one fetch, many tabs slice it client-side" shape as `doc` itself.
// onLoadingStart/onLoadingEnd (not a plain setLoading(bool)) - see
// PlatformReport.js's own beginLoading/endLoading: this component's two
// effects below (loanbook, rating tables) run concurrently with each other
// and with that parent's own loads, and a raw shared boolean setter break
// as soon as any two of them overlap (confirmed: whichever finished first
// turned the overlay off while the other kept running). These two are
// already the parent's reference-counted pair, so calling them directly
// here composes correctly with everything else without this component
// needing its own counting.
export default function LendDocTabs({ doc, onLoadingStart = () => {}, onLoadingEnd = () => {} }) {
    const [activeTab, setActiveTab] = useState(0);
    const [loanbook, setLoanbook] = useState(null);
    const [loanbookLoading, setLoanbookLoading] = useState(false);
    const [ratingTables, setRatingTables] = useState(null);
    const [ratingTablesLoading, setRatingTablesLoading] = useState(false);
    const { theme } = useTheme();
    // Mutates SEGMENT_COLORS in place (see its own declaration above) so
    // every Lend chart's segment-keyed color - read live via property
    // access or the *_SERIES arrays' `get color()` getters - reflects the
    // current theme. PlatformReport.js separately calls the shared
    // chart_theme.js's applyChartJsTheme/applyChartColorsTheme for its own
    // CHART_COLORS-based charts; this is SEGMENT_COLORS' Lend-only
    // equivalent, kept local to this file for the same reason SEGMENT_COLORS
    // itself is (see its comment).
    useEffect(() => {
        applyLendColorsTheme(theme);
    }, [theme]);

    useEffect(() => {
        if (!doc?.report_date) return;
        let cancelled = false;
        setLoanbookLoading(true);
        // Same global overlay+spinner every other loading state in this app
        // uses now (see PlatformReport.js's loadGenerations/loadOverview) -
        // loanbookLoading itself is kept too, still gating this tab's own
        // in-place fallback for a failed/empty fetch, just no longer the
        // only visible loading indicator.
        onLoadingStart();
        axiosWithAuth
            .get('/lend-loanbook', { params: { report_date: doc.report_date } })
            .then((res) => {
                if (!cancelled) setLoanbook(res.data);
            })
            .catch(() => {
                if (!cancelled) setLoanbook(null);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoanbookLoading(false);
                    onLoadingEnd();
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc?.report_date]);

    // Static reference tables (S&P/Fitch default-rate bands) - not
    // report_date-scoped, so fetched once regardless of which report_date is
    // selected, unlike the loanbook fetch above. See lend_loanbook.js's
    // /rating-tables.
    useEffect(() => {
        let cancelled = false;
        setRatingTablesLoading(true);
        onLoadingStart();
        axiosWithAuth
            .get('/lend-loanbook/rating-tables')
            .then((res) => {
                if (!cancelled) setRatingTables(res.data);
            })
            .catch(() => {
                if (!cancelled) setRatingTables(null);
            })
            .finally(() => {
                if (!cancelled) {
                    setRatingTablesLoading(false);
                    onLoadingEnd();
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tabs = buildTabsForLendDoc(doc, loanbook, loanbookLoading, ratingTables, ratingTablesLoading);
    if (!doc) return <LoadingState label="Loading report" />;
    // scrollable: this tab set has grown to ~19 tabs (one per PBIX page/
    // group) - without it, tabs past the container width are simply
    // unreachable (no scroll affordance), which is exactly the "can't see
    // the tabs after Default Rates per Vintage" bug reported against this
    // component.
    return (
        <TabView scrollable className="pr-report-tabs" activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
            {tabs.map((tab, i) => (
                <TabPanel key={tab.key} header={tab.header}>
                    {activeTab === i && tab.content}
                </TabPanel>
            ))}
        </TabView>
    );
}
